import { useState } from "react";

import { CatalogueWriteError, useSaveItem } from "../api";
import type {
  CatalogueItem,
  CatalogueKind,
  CatalogueWrite,
  ImpactLine,
} from "../types";

interface PendingConfirmation {
  id: string | null;
  body: CatalogueWrite;
  lines: ImpactLine[];
  token: string;
  /** The server refused the confirmation: the numbers moved since. */
  stale: boolean;
}

export type SaveOutcome =
  | { status: "saved"; item: CatalogueItem }
  | { status: "confirming" }
  | { status: "invalid"; errors: Record<string, string[]> }
  | { status: "failed"; message?: string };

/**
 * Save a row, with the impact confirmation the server may ask for.
 *
 * An edit that changes what existing companies see is refused with 409 and the
 * numbers to show. Nothing is saved until the administrator confirms; the
 * confirmation resends the same body with the token naming those numbers, and
 * a second 409 means someone changed the row in between — the fresh numbers
 * are shown again rather than saving on the stale ones.
 */
export function useSaveFlow(kind: CatalogueKind, onSaved: (item: CatalogueItem) => void) {
  const save = useSaveItem(kind);
  const [confirming, setConfirming] = useState<PendingConfirmation | null>(null);

  const attempt = async (
    id: string | null,
    body: CatalogueWrite,
    stale: boolean,
  ): Promise<SaveOutcome> => {
    try {
      const item = (await save.mutateAsync({ id, body })) as CatalogueItem;
      setConfirming(null);
      onSaved(item);
      return { status: "saved", item };
    } catch (error) {
      if (error instanceof CatalogueWriteError) {
        if (error.code === "confirmation_required" && error.impact) {
          const { confirm_token: _token, ...rest } = body;
          void _token;
          setConfirming({
            id,
            body: rest,
            lines: error.impact.lines,
            token: error.impact.token,
            stale,
          });
          return { status: "confirming" };
        }
        if (error.errors) {
          setConfirming(null);
          return { status: "invalid", errors: error.errors };
        }
        setConfirming(null);
        return { status: "failed", message: error.detail };
      }
      setConfirming(null);
      return { status: "failed" };
    }
  };

  return {
    isPending: save.isPending,
    confirming,
    submit: (id: string | null, body: CatalogueWrite) => attempt(id, body, false),
    confirm: () =>
      confirming
        ? attempt(
            confirming.id,
            { ...confirming.body, confirm_token: confirming.token },
            true,
          )
        : Promise.resolve<SaveOutcome>({ status: "failed" }),
    dismiss: () => setConfirming(null),
  };
}

